import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { isEmpty } from 'lodash';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import { GET_USER_FABRIC_FAVORITES } from 'mocks/queries';
import {
  ADD_TO_USER_FABRIC_FAVS,
  REMOVE_FABRIC_FROM_USER_FABRIC_FAVS,
} from 'mocks/mutations';
import useUserData from 'data-hooks/useUserData';
import useAllOptions from 'data-hooks/useAllOptions';

const useFabricFavs = (hookOptions = {}) => {
  // state
  const [fabricFavsInfo, setFabricFavsInfo] = useState({});
  const { loadMaterialDetails } = hookOptions;

  const { allOptionsValues } = useAllOptions();

  const { loading, data: userFabricFavs, refetch } = useSuspenseQuery(
    GET_USER_FABRIC_FAVORITES,
    {
      ssr: false,
    }
  );

  const { isLoggedIn, userId } = useUserData();

  const updateUserFav = (cache, updatedFabricFavData, action) => {
    const existingUserFavs = cache.readQuery({
      query: GET_USER_FABRIC_FAVORITES,
    });

    if (existingUserFavs && updatedFabricFavData) {
      cache.writeQuery({
        query: GET_USER_FABRIC_FAVORITES,
        data: {
          userFabricFavs: {
            ...updatedFabricFavData,
            ...(action === 'add' &&
              Object.keys(existingUserFavs.userFabricFavs).length &&
              existingUserFavs.userFabricFavs),
          },
        },
      });
    }
  };

  const [addToUserFabricFavorites] = useMutation(ADD_TO_USER_FABRIC_FAVS, {
    update(cache, { data }) {
      const favs = data.userFabricFavs;
      Object.keys(favs).forEach(key => key === 'undefined' && delete favs[key]);
      updateUserFav(cache, favs, 'add');
    },
  });
  const [removeFromUserFabricFavorites] = useMutation(
    REMOVE_FABRIC_FROM_USER_FABRIC_FAVS,
    {
      update(cache, { data }) {
        const favs = data.userFabricFavs;
        Object.keys(favs).forEach(
          key => key === 'undefined' && delete favs[key]
        );
        updateUserFav(cache, favs, 'remove');
      },
    }
  );

  // effects
  useEffect(() => {
    refetch();
  }, [refetch, isLoggedIn]);

  useEffect(() => {
    if (
      !loadMaterialDetails ||
      isEmpty(userFabricFavs?.userFabricFavs) ||
      isEmpty(allOptionsValues)
    ) {
      return;
    }
    const favFabricsInfoTemp = {};
    for (const key of Object.keys(userFabricFavs?.userFabricFavs)) {
      if (!isEmpty(allOptionsValues[key])) {
        favFabricsInfoTemp[key] = { ...allOptionsValues[key], materialId: key };
      }
    }

    setFabricFavsInfo(favFabricsInfoTemp);
  }, [allOptionsValues, loadMaterialDetails, userFabricFavs?.userFabricFavs]);

  const setOptionAsFav = async optionId => {
    if (userFabricFavs?.[optionId]) {
      return;
    }

    if (!isLoggedIn) {
      addToUserFabricFavorites({
        variables: {
          materialId: optionId,
        },
      });
      return;
    }

    addToUserFabricFavorites({
      variables: {
        userId,
        materialId: optionId,
      },
    });
  };

  const removeOptionAsFav = async optionId => {
    if (!isLoggedIn) {
      removeFromUserFabricFavorites({
        variables: {
          materialId: optionId,
        },
      });
      return;
    }
    removeFromUserFabricFavorites({
      variables: {
        userId,
        materialId: optionId,
      },
    });
  };

  return {
    data: {
      userFabricFavs: loading ? {} : userFabricFavs?.userFabricFavs,
      isUserLoggedIn: isLoggedIn,
      fabricFavsInfo,
    },
    actions: { setOptionAsFav, removeOptionAsFav },
  };
};

export default useFabricFavs;
